/**
 * THE ROAD BACK INTO THE CLUB — offered on our own pages, to the visitor who walked out
 * of one.
 *
 * `./directoryReturn.ts` is this rule pointing the other way: it remembers that a
 * visitor reached a club's page from our catalogue, and gives him the way back to
 * `/clubs`. This file remembers that he reached `/players` — or `/tournaments`, or
 * `/ladders`, or `/clubs` itself — from a club's storefront, and gives him the way back
 * to the club. Same mechanism (`./tabMemory.ts`), same lifetime, opposite direction.
 *
 * THE OWNER'S FINDING, 2026-09-07. Standing on a club's storefront and tapping a menu
 * row took him to the platform-wide list and said nothing about the club he had just
 * left. `205a7913` renamed those rows "All tournaments / All ladders / …" so the
 * DESTINATION stops lying; this is the other half — the ORIGIN stops vanishing. He was
 * shown the case against carrying a club into our pages twice, and chose it anyway.
 *
 * WHAT IS REMEMBERED IS ARRIVAL, NOT MEMBERSHIP, and that is the whole reach of the
 * feature. A player who belongs to three clubs and a guest who has never joined one are
 * treated identically: both were reading a club's page a second ago, both are owed the
 * way back. Nothing here asks whether the viewer is a member, and nothing here may start
 * to — a "my clubs" rule would drop precisely the guest this exists for.
 *
 * ONE SLUG, AND THE IDENTITY COMES FROM THE STORE THAT ALREADY HAS IT. The club's name
 * and mark are not copied here: they are read back out of `./storefront.ts`, the store
 * every public club read already writes and the chrome already renders from. So there is
 * one club record in the tab, not two that could disagree.
 *
 * EXCEPT RIGHT AFTER A RELOAD (owner, 2026-10-16: "the club stays in the header after a
 * reload too"). The slug now lives as long as the tab (`./tabMemory.ts`), but the store
 * still empties on reload. So the header would have a slug and nothing to draw it with,
 * and would show our wordmark until some club read happened to arrive. To bridge that
 * gap, the narrow part the header draws (slug, name, mark) is kept for the tab as well.
 * It is used ONLY while the store has no answer for that slug. A live read always wins,
 * and `./storefrontReturnCheck.ts` asks the server once per page load and forgets a
 * club that is gone, so a stale note falls back to the wordmark rather than a broken bar.
 *
 * NO COLOUR CROSSES. `StorefrontIdentity` carries the club's accent, and
 * `./storefrontTheme.ts` re-tints `--accent` from it on the club's own storefront. A
 * platform page is OURS: it carries the club's mark and name and keeps platform colours.
 * The projection below is narrow for that reason and not for economy — there is no
 * accent on it to reach a platform page even by accident.
 */

import { clubStorefrontHref } from './storefrontClubSwitch'
import { clubOwningPath } from './storefrontRoutes'
import { readKnownStorefront } from './storefront'
import { clubSlugSlot, readTabValue, writeTabValue } from './tabMemory'

/** The club's storefront this tab last stood on. Written by `StorefrontShell` — the one
 *  component that mounts only when a real club page is really being shown — rather than
 *  by each link out of it, so every way out is covered: the drawer, the footer, a link
 *  in the club's own copy, a tap on the browser's own history. The write lands while the
 *  storefront is on screen, which is always BEFORE the click that leaves it, so the
 *  platform header can read this straight through during render. The changes that
 *  arrive later, a club refreshed or forgotten by the server's answer, are announced
 *  through `subscribeStorefrontReturn`. */
const lastStorefront = clubSlugSlot('trc:storefront-return:slug')

/** What the header draws, kept for the tab so a reload can draw it before any read. */
type TabClub = { slug: string; name: string; logoUrl: string }
const TAB_CLUB_KEY = 'trc:storefront-return:club'

function parseTabClub(raw: string): TabClub | null {
  try {
    const value = JSON.parse(raw) as Partial<TabClub> | null
    if (typeof value?.slug !== 'string' || typeof value.name !== 'string' || !value.name) return null
    return { slug: value.slug, name: value.name, logoUrl: typeof value.logoUrl === 'string' ? value.logoUrl : '' }
  } catch {
    return null
  }
}

let tabClub = parseTabClub(readTabValue(TAB_CLUB_KEY))
const listeners = new Set<() => void>()
let version = 0

function announce(): void {
  version += 1
  listeners.forEach((listener) => listener())
}

export function rememberStorefrontVisit(slug: string | null | undefined): void {
  lastStorefront.remember(slug)
  const club = readKnownStorefront(lastStorefront.read())
  if (!club?.name) return
  const next = { slug: club.slug, name: club.name, logoUrl: club.logoUrl }
  const before = JSON.stringify(tabClub)
  tabClub = next
  if (JSON.stringify(next) === before) return
  writeTabValue(TAB_CLUB_KEY, JSON.stringify(next))
  announce()
}

/** Drop the remembered club, in memory and in the tab, and redraw the header. */
export function forgetStorefrontVisit(): void {
  lastStorefront.forget()
  tabClub = null
  writeTabValue(TAB_CLUB_KEY, '')
  announce()
}

export function subscribeStorefrontReturn(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Changes whenever the remembered club changes or is forgotten; for `useSyncExternalStore`. */
export function storefrontReturnVersion(): number {
  return version
}

/** The remembered slug the header can draw only from the tab's note, because no live
 *  read has named it this page load. `''` when there is nothing to confirm. */
export function storefrontReturnToConfirm(): string {
  const slug = lastStorefront.read()
  return slug && !readKnownStorefront(slug) ? slug : ''
}

function drawableClub(slug: string): TabClub | null {
  const live = readKnownStorefront(slug)
  if (live) return live
  return tabClub && lastStorefront.holds(tabClub.slug) ? tabClub : null
}

/** The storefront's exits OUTSIDE the player surfaces that still keep the club (/ux
 *  storefront-exits §3.1, §3.2 C, 2026-09-26): the board "Find a game" lands on, which is
 *  app chrome, and our Privacy and Terms, linked from the club's own footer. Everything
 *  else in the app and marketing chrome keeps our wordmark — a manager's workspace or a
 *  viewer's profile is theirs, reached from the avatar menu, not from the club's menu. */
const KEEPS_THE_CLUB = ['/play/games', '/privacy', '/terms']

export function keepsStorefrontClub(pathname: string): boolean {
  return KEEPS_THE_CLUB.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}

/** What a platform page may say about where its visitor came from: the club's mark, its
 *  name and its address. No accent — see the note above. */
export type StorefrontReturnTarget = {
  slug: string
  name: string
  logoUrl: string
  href: string
}

/**
 * The way back, or `null` for "say nothing" — which is the answer for a visitor who
 * arrived from search, from a forwarded link, from a bookmark, or in a fresh tab. A reload
 * in the same tab keeps the club (owner, 2026-10-16). `null` is the ordinary answer and the header it produces is the header this
 * site has today, unchanged.
 *
 * AND `null` ON ANY ADDRESS THAT BELONGS TO ANOTHER CLUB, which is not a nicety. A club's page
 * usually wears its own chrome and never comes near this function — but only once the
 * club's read has landed; before that, and for a club with no public page at all, the
 * ordinary player header renders at `/clubs/<slug>`. Without this line a visitor walking
 * from one club to the next would see the FIRST club's mark sitting on the second club's
 * page for as long as the read took. A club's page naming another club is the one thing
 * the whole storefront exists to prevent, so it is refused by address and not left to
 * timing. THE SAME CLUB'S OWN PAGES ARE NOT REFUSED (/ux storefront-exits §3.3, 2026-09-26):
 * a member opening his bill from the club's Membership tab is still in that club, and
 * the bar says so. A bill opened cold from the email remembers nothing and keeps the
 * wordmark.
 */
export function storefrontReturnTarget(pathname: string): StorefrontReturnTarget | null {
  const slug = lastStorefront.read()
  if (!slug) return null
  const owner = clubOwningPath(pathname)
  if (owner && !lastStorefront.holds(owner)) return null
  // A slug with no identity behind it cannot be drawn — there is no name to print and no
  // mark to print it beside — so it is the same answer as no slug at all.
  const club = drawableClub(slug)
  if (!club || !club.name) return null
  return {
    slug: club.slug,
    name: club.name,
    logoUrl: club.logoUrl,
    href: clubStorefrontHref(club.slug),
  }
}
